/**
 * wizardMatcher.js
 *
 * Pure-JS predicate + count service for the wizard live footer. Builds a
 * predicate from current wizard selections and counts matches grouped by
 * category. Intentionally permissive: missing selections (null/empty)
 * skip that criterion, so partial wizard state always returns a useful
 * "tools match so far" total.
 *
 * This is DIFFERENT from bitSelectionService's strict predicate — that
 * one is used for the final results query and requires every criterion.
 *
 * Data shape notes:
 *   - products-tophammer.json: top-level `subcategory` (e.g. 'drill-bits'),
 *     `productLine` ('top-hammer'), `thread` (e.g. 'R32'). TH bits carry
 *     `bitDiameterMm` (top-level, camelCase, audited 17 May 2026).
 *   - products-dth.json: top-level `category` (e.g. 'dth-bits',
 *     'dth-hammers'), `productLine` ('dth'). DTH bits carry
 *     `bitDiameter_mm` (top-level, snake_case). Shank-type filter uses
 *     the top-level `shankType` field (~700 items carry it).
 *
 * Product JSONs are loaded async via dataService.loadProducts() — static
 * imports from /public/ break Vite. Call ensureProductsLoaded() at mount
 * time before invoking countMatchesByCategory.
 *
 * @module services/wizardMatcher
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D6)
 */

import { loadProducts } from '@services/dataService';

// Map wizard productLine (uppercase 'TH' | 'DTH' — matches DRILLING_METHODS.id
// and Sandvik item-class codes) to the lowercase productLine value stored
// in the product JSON files.
const PRODUCT_LINE_MAP = { TH: 'top-hammer', DTH: 'dth' };

// Module-level caches populated by ensureProductsLoaded(). Until populated,
// countMatchesByCategory returns { total: 0, byCategory: {} } so the live
// footer shows "0 tools match so far" while data is in flight.
let _productsTH = null;
let _productsDTH = null;
let _loadPromise = null;

/**
 * loadProducts() returns either { products, categories } or a bare array
 * (the shape has drifted across data revisions). Normalise to a flat array.
 */
function asProductArray(envelope) {
  if (Array.isArray(envelope)) return envelope;
  if (envelope && Array.isArray(envelope.products)) return envelope.products;
  return [];
}

/**
 * Lazily fetch the TH + DTH product JSONs and stash them in module-level
 * caches. Idempotent: concurrent callers share the same promise.
 *
 * @returns {Promise<void>}
 */
export async function ensureProductsLoaded() {
  if (_productsTH && _productsDTH) return;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    try {
      const [th, dth] = await Promise.all([
        loadProducts('tophammer'),
        loadProducts('dth'),
      ]);
      _productsTH = asProductArray(th);
      _productsDTH = asProductArray(dth);
    } catch (err) {
      console.warn('[wizardMatcher] product load failed:', err?.message);
      _productsTH = _productsTH || [];
      _productsDTH = _productsDTH || [];
    } finally {
      _loadPromise = null;
    }
  })();

  return _loadPromise;
}

/**
 * Test-only injection point. Lets unit tests skip the async fetch and
 * supply fixture arrays directly.
 */
export function setProductsForTest(th, dth) {
  _productsTH = th;
  _productsDTH = dth;
}

/**
 * Best-effort numeric extraction. Looks at top-level field first, then
 * inside specifications / dimensions nested objects (DTH dimensions
 * structure differs from TH).
 */
function numericField(product, ...candidateKeys) {
  for (const k of candidateKeys) {
    const direct = product[k];
    if (typeof direct === 'number') return direct;
    const spec = product.specifications?.[k];
    if (typeof spec === 'number') return spec;
    const dim = product.dimensions?.[k];
    if (typeof dim === 'number') return dim;
  }
  return null;
}

// re-export for diameter consumers (e.g. D5 histogram)
export function diameterOf(product) {
  // TH carries both `diameterMm` (rods/shanks/couplings — top-of-range
  // ~152mm) and `bitDiameterMm` (bits — top-of-range ~254mm). DTH uses
  // `bitDiameter_mm` (top-level) or `dimensions.bitDiameter_mm`.
  return numericField(
    product,
    'diameterMm',
    'bitDiameterMm',
    'bitDiameter_mm',
    'holeDiameter_mm',
    'diameter_mm',
    'diameter'
  );
}

/**
 * Returns the cached product array for a given productLine, or null when
 * the caches are cold. Caller should await ensureProductsLoaded() first.
 *
 * @param {'TH'|'DTH'} productLine
 * @returns {Array<Object>|null}
 */
export function getProductsForLine(productLine) {
  if (productLine === 'TH') return _productsTH;
  if (productLine === 'DTH') return _productsDTH;
  return null;
}

/**
 * Build a predicate from current wizard selections.
 * Permissive: if a selection is null/empty, that criterion is skipped.
 * Likewise, if a product doesn't carry the field we're filtering on,
 * we don't reject it — we only reject products whose field value is
 * present and out of range.
 *
 * @param {Object} selections - Wizard selections (from WizardContext).
 * @returns {(product: Object) => boolean} Predicate.
 */
export function buildPredicate(selections) {
  return (p) => {
    if (selections.productLine) {
      const expected = PRODUCT_LINE_MAP[selections.productLine];
      if (p.productLine !== expected) return false;
    }
    if (selections.application && p.application_c) {
      if (p.application_c !== selections.application) return false;
    }
    if (selections.holeDiameter != null) {
      // D5 REVERT: holeDiameter is a single mm number (number | null). Only
      // applies to drill-bits — non-bit categories (rods, shanks, couplings)
      // carry their own diameterMm that is not the user's chosen hole size.
      const isBit =
        p.subcategory === 'drill-bits' ||
        p.category === 'drill-bits' ||
        p.category === 'dth-bits';
      if (isBit) {
        const bitDia =
          p.bitDiameterMm ??
          p.bitDiameter_mm ??
          p?.dimensions?.bitDiameter_mm;
        if (bitDia != null && bitDia !== selections.holeDiameter) return false;
      }
    }
    if (selections.threadSystem) {
      // TH JSON uses `thread`; tolerate both `thread` and `threadSystem`.
      const pThread = p.threadSystem || p.thread;
      if (pThread && pThread !== selections.threadSystem) return false;
    }
    if (selections.shankType) {
      // DTH JSON: discovered via products-dth.json field audit (17 May 2026):
      //   `shankType`  — primary shank value carried by ~700 DTH items (bits,
      //                  drill tubes, drill pipes, hammer-internal parts).
      //   `compatibleShankType` — hammer-side list of supported shanks; not
      //                  used here because we filter the catalog by the user's
      //                  *own* shank, not by a hammer's compatibility.
      // Permissive: products without the field are not rejected.
      const pShank = p.shankType || p.shank_type;
      if (pShank && pShank !== selections.shankType) return false;
    }
    return true;
  };
}

/**
 * Count matches grouped by category for the live footer.
 *
 * Synchronous: reads from the module-level product caches populated by
 * ensureProductsLoaded(). If the caches are empty (data not yet loaded),
 * returns { total: 0, byCategory: {} } so the footer renders cleanly.
 *
 * @param {Object} selections - Wizard selections.
 * @returns {{total: number, byCategory: Object<string, number>}}
 */
export function countMatchesByCategory(selections) {
  if (!_productsTH || !_productsDTH) {
    return { total: 0, byCategory: {} };
  }

  const predicate = buildPredicate(selections);
  const products = selections.productLine === 'DTH'
    ? _productsDTH
    : selections.productLine === 'TH'
      ? _productsTH
      : [..._productsTH, ..._productsDTH];

  const byCategory = {};
  let total = 0;
  for (const p of products) {
    if (predicate(p)) {
      // Field-name fallback chain:
      //   DTH products carry `category` (e.g. 'dth-hammers', 'dth-bits',
      //     'dth-drill-pipes').
      //   TH products carry `pgmjDesc` (e.g. 'TH Bits', 'TH Rods') as the
      //     primary grouping label; finer `subcategory` (17 kebab-case slugs)
      //     is also present but too noisy for a 5-bucket footer.
      //   Both carry `categoryLevel3` as a human-readable mid-level label.
      const cat = p.category || p.pgmjDesc || p.categoryLevel3 || p.subcategory || 'other';
      byCategory[cat] = (byCategory[cat] || 0) + 1;
      total += 1;
    }
  }
  return { total, byCategory };
}

/**
 * Convert a category slug like "th-coupling-sleeves" to a human label
 * "Coupling Sleeves" (drops the th-/dth-/rh<digits>- prefix).
 *
 * @param {string} slug
 * @returns {string}
 */
export function humaniseCategory(slug) {
  if (!slug) return '';
  // Human-readable labels (already contain a space) pass through unchanged.
  if (/\s/.test(slug)) return slug;
  return slug
    .replace(/^th-|^dth-|^rh\d+-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
